import { useState } from 'react';
import { Link } from 'react-router-dom';
import { register, resendVerification } from '../api/client';
import { useI18n } from '../i18n';
import { inlineError } from '../hooks/useApiCall';
import { useBusy } from '../hooks/useBusy';
import { usePageTitle } from '../hooks/usePageTitle';
import { record } from '../observability';
import AuthCard, { LogoIcon } from '../components/auth/AuthCard';
import AuthResult from '../components/auth/AuthResult';
import Notice from '../components/shared/Notice';
import Field from '../components/shared/Field';

export default function Register() {
  const { t } = useI18n();
  usePageTitle(t.register.title);
  const [form, setForm] = useState({
    email: '', password: '', confirm_password: '',
    first_name: '', last_name: '',
    gender: '', birth_date: '', phone: ''
  });
  const [error, setError] = useState('');
  // #665 (F66) — the two passwords differ. A flag, not a text: the text follows the language of the page.
  const [mismatch, setMismatch] = useState(false);
  // #605, #664 — the lock of Create account: a second tap sends no second sign-up.
  const signUp = useBusy();
  const [emailSent, setEmailSent] = useState(false);
  // #605 — the lock of the resend: a second tap sends no second email.
  const resend = useBusy();
  // #605 — the resend worked. The next click clears it.
  const [resent, setResent] = useState(false);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    // An edit of either password field removes the mismatch error: the next submit checks the pair again.
    if (k === 'password' || k === 'confirm_password') setMismatch(false);
    setForm(f => ({ ...f, [k]: e.target.value }));
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const differ = form.password !== form.confirm_password;
    setMismatch(differ);
    if (differ) { setError(''); return; }
    await signUp.run(async () => {
      setError('');
      try {
        await register({
          email: form.email, password: form.password,
          first_name: form.first_name, last_name: form.last_name,
          gender: form.gender ? (form.gender as 'Male' | 'Female') : undefined,
          birth_date: form.birth_date || undefined,
          phone: form.phone || undefined,
        });
        record({ event: 'registration_submitted', props: { has_phone: form.phone ? '1' : '0' } });
        setEmailSent(true);
      } catch (err) {
        setError(inlineError(err));
      }
    });
  }

  async function handleResend() {
    await resend.run(async () => {
      setError('');
      setResent(false);
      try {
        await resendVerification(form.email);
        // #605 — before, a resend that worked changed nothing on the screen.
        setResent(true);
      } catch (err) { setError(inlineError(err)); }   // a failed resend showed nothing (2026-09-19)
    });
  }

  // ── Email sent confirmation screen ──
  if (emailSent) {
    return (
      <AuthCard>
        <AuthResult icon={LogoIcon} title={t.auth.checkEmail}
          text={<>{t.auth.verificationSent}<br /><span className="font-medium text-gray-900 break-all">{form.email}</span></>}
          action={{ to: '/login', label: t.auth.backToLogin, variant: 'text' }}>
          {/* #504 review — a failed resend set the error, and this screen did not show it. */}
          {error && <Notice tone="error">{error}</Notice>}
          {resent && <Notice tone="success" data-testid="resend-done">{t.auth.verificationResent}</Notice>}
          <button onClick={handleResend} disabled={resend.busy} className="btn-outline btn-sm w-full">
            {resend.busy ? t.common.loading : t.auth.resendLink}
          </button>
        </AuthResult>
      </AuthCard>
    );
  }

  // ── Registration form ──
  return (
    <AuthCard wide title={t.home.createAccount} subtitle={t.home.title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t.profile.firstName} required>
            <input className="input" value={form.first_name} onChange={set('first_name')} data-testid="register-first-name" />
          </Field>
          <Field label={t.profile.lastName} required>
            <input className="input" value={form.last_name} onChange={set('last_name')} data-testid="register-last-name" />
          </Field>
        </div>

        <Field label={t.home.email} required>
          <input type="email" className="input" value={form.email} onChange={set('email')} data-testid="register-email" />
        </Field>

        <Field label={t.home.password} required help={t.home.passwordRequirements}>
          <input type="password" minLength={8} className="input"
            value={form.password} onChange={set('password')} data-testid="register-password" />
        </Field>

        {/* #665 (F66) — the mismatch is the error of the confirm field: the field gets aria-invalid. */}
        <Field label={t.home.confirmPassword} required error={mismatch ? t.home.passwordsMismatch : undefined}
          errorTestId="register-mismatch">
          <input type="password" className="input"
            value={form.confirm_password} onChange={set('confirm_password')} data-testid="register-confirm-password" />
        </Field>

        <Field label={t.profile.phone}>
          <input type="tel" className="input" value={form.phone} onChange={set('phone')}
            placeholder="+370..." data-testid="register-phone" />
        </Field>

        {error && <Notice tone="error">{error}</Notice>}

        <button type="submit" disabled={signUp.busy} className="btn-primary w-full py-2.5" data-testid="register-submit">
          {signUp.busy ? t.common.loading : t.home.createAccount}
        </button>
        {/* #529 — the notice at collection (GDPR Art. 13). */}
        <p data-testid="privacy-notice" className="text-xs text-gray-500 text-center">
          {t.home.privacyNotice}{' '}
          <Link to="/manual#privacy" className="underline hover:text-gray-900">{t.home.privacyLink}</Link>
        </p>
        <p data-testid="terms-notice" className="text-xs text-gray-500 text-center">
          {t.home.termsNotice}{' '}
          <Link to="/manual#terms" className="underline hover:text-gray-900">{t.home.termsLink}</Link>
        </p>
      </form>

      <p className="text-center text-sm text-gray-500">
        {t.home.alreadyHaveAccount}{' '}
        <Link to="/login" className="text-gray-900 font-medium hover:underline">{t.nav.login}</Link>
      </p>
    </AuthCard>
  );
}
